import { Component, TemplateRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ProductsFilterStateService, formatPremium } from '../state/products-filter-state.service';
import { SURR_YEARS_LABELS, SURR_YEARS_UNLIMITED, surrYearsLabel } from '../models/product-search.models';
import { SessionContextService } from '../../../core/services/session-context.service';
import { CaseContextService } from '../../../core/services/case-context.service';
import { CaseApiService } from '../../../core/services/case-api.service';
import { Case, CaseInput, RatingFloor } from '../../../core/services/case.model';
import { STATES_LIST } from '../../rates/config/filter-options';
import { ProductRailGroupComponent } from './groups/product-rail-group.component';
import { ProductChecklistPickerComponent } from './pickers/product-checklist-picker.component';
import { StateAvailabilityGridComponent } from '../../../shared/state-availability/state-availability-grid.component';
import { ProductPremiumInputComponent } from './pickers/product-premium-input.component';
import { FilterRailGroupsComponent } from './filter-rail-groups.component';
import { PRODUCTS_FILTER_MANIFEST } from './products.filter-manifest';
import { CompanyProductPickerComponent } from '../../../shared/company-product-picker/company-product-picker.component';
import { CarrierProductFlyOutService } from '../../../shared/company-product-picker/carrier-product-flyout.service';
import { RailLayoutService } from '../../../shell/rail-host/rail-layout.service';
import { CasePickerFlyoutComponent } from '../../../shared/case-box/case-picker-flyout.component';
import type {
  PickerApplyResult,
  PickerCarrier,
  PickerData,
  PickerProduct,
} from '../../../shared/company-product-picker/company-product-picker.types';

interface ProductType {
  id: 'FA' | 'IA' | 'VA' | 'RILA';
  label: string;
}

/**
 * Menu-axis Phase 4.2 — ProductSearchRailComponent.
 *
 * First net-new consumer of ProductsFilterStateService (4.1). Rendered
 * by Phase 2's RailHostComponent when /products route declares
 * `data: { rail: ProductSearchRailComponent }` and
 * `sessionCtx.enableHorizontalNav()` is true.
 *
 * Differs from RatesRailComponent (3b) in two key ways per the
 * menu_axis_products_v1 mockup:
 *   1. Type pills are MULTI-SELECT (not single-active tabs). Click
 *      toggles inclusion in filterState.activeTypes Set. Count badge
 *      shows products available within each type AT the current
 *      filter intersection.
 *   2. No per-tab state — /products is cross-product by design.
 *
 * Phase 4.2 ships skeleton + type pills + search input. Subsequent
 * sub-tasks (4.3 chips + pickers, 4.4 toggle pills, 4.5 My Views,
 * 4.6 page-header cluster) build on this scaffold.
 */
@Component({
  selector: 'app-product-search-rail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ProductRailGroupComponent,
    ProductChecklistPickerComponent,
    StateAvailabilityGridComponent,
    ProductPremiumInputComponent,
    CompanyProductPickerComponent,
    FilterRailGroupsComponent,
    CasePickerFlyoutComponent,
  ],
  templateUrl: './product-search-rail.component.html',
  styleUrl: './product-search-rail.component.scss',
})
export class ProductSearchRailComponent {
  protected readonly filterState = inject(ProductsFilterStateService);
  protected readonly sessionCtx = inject(SessionContextService);
  private readonly flyoutSvc = inject(CarrierProductFlyOutService);
  private readonly railLayout = inject(RailLayoutService);
  private readonly caseCtx = inject(CaseContextService);
  private readonly caseApi = inject(CaseApiService);
  private readonly router = inject(Router);

  // ── Criteria section — case-as-saved-search (P3) ──────────────────
  // Same case knobs as /rates. State/Term/Rating drive the product filters;
  // Age & Premium have no product filter (context-only, flagged in the UI) but
  // stay editable + saved — they're the client's criteria. Persona = client-less.
  protected readonly caseName = computed(() => this.caseCtx.activeCase()?.name ?? '');
  protected readonly caseLoaded = computed(() => !!this.caseCtx.activeCase());
  protected readonly criteriaOpen = signal(true);
  toggleCriteria(): void { this.criteriaOpen.update((v) => !v); }

  /** "Advanced filters" section collapse. Default CLOSED (2026-06-11) — only
   *  Criteria (cases) opens on load, case or no case. */
  protected readonly advancedOpen = signal(false);
  toggleAdvanced(): void { this.advancedOpen.update((v) => !v); }

  // ── Architectural prototype (2026-06-11) — manifest-driven Advanced filters.
  // Default OFF: the live rail renders the hand-written groups, unchanged.
  // Flip to compare: localStorage.setItem('bn.manifestRail','1'); reload.
  // Proves one generic FilterRailGroups renders the groups from a manifest.
  protected readonly manifestRail = signal(this.readManifestFlag());
  protected readonly productsManifest = PRODUCTS_FILTER_MANIFEST;
  /** Bound option-lookup handed to the generic component (key → string[]). */
  protected readonly optionsForFn = (key: string): string[] => this.options(key);
  private readonly carrierTpl = viewChild<TemplateRef<unknown>>('carrierTpl');
  private readonly statusTpl = viewChild<TemplateRef<unknown>>('statusTpl');
  /** 'slot' dim templates the generic component projects, keyed by dim.slot. */
  protected readonly railSlots = computed<Record<string, TemplateRef<unknown> | undefined>>(() => ({
    carrier: this.carrierTpl(),
    status: this.statusTpl(),
  }));
  private readManifestFlag(): boolean {
    try { return localStorage.getItem('bn.manifestRail') === '1'; } catch { return false; }
  }

  // ── Audience split (P4) — mirrors the rates rail ──────────────────
  // Brokers (userGroup 1/3) get Criteria + hide My Views; analysts /
  // back-office (0/2) get My Views + hide Criteria; Beacon admin OR no
  // session see both (preview/config).
  private readonly userGroup = computed<number | null>(() => this.sessionCtx.context()?.userGroup ?? null);
  private readonly isBrokerAudience = computed<boolean>(() => {
    const g = this.userGroup();
    return g === 1 || g === 3;
  });
  protected readonly showCriteria = computed<boolean>(() =>
    this.sessionCtx.isBeaconAdmin() || this.userGroup() == null || this.isBrokerAudience());

  readonly statesList = STATES_LIST;
  private readonly AM_BEST_ORDER = ['A++', 'A+', 'A', 'A-', 'B++', 'B+', 'B', 'B-', 'C++', 'C+', 'C', 'C-', 'D'];
  private applyNow(): void { this.filterState.requestApplyFilters(); }
  private numOrNull(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v); return Number.isFinite(n) ? n : null;
  }

  // Filtering knobs → products filter state.
  get kState(): string { this.filterState.version(); return this.filterState.selectedStates[0] ?? ''; }
  set kState(v: string) { this.filterState.selectedStates = v ? [v] : []; this.applyNow(); }
  get kTermMin(): number | null { this.filterState.version(); return this.surrRange().min; }
  set kTermMin(v: number | null) { this.setSurrRange(this.numOrNull(v), this.surrRange().max); }
  get kTermMax(): number | null { this.filterState.version(); return this.surrRange().max; }
  set kTermMax(v: number | null) { this.setSurrRange(this.surrRange().min, this.numOrNull(v)); }
  get kRating(): string { this.filterState.version(); return this.ratingFloorFromSet(this.filterState.selectedAmBest) ?? ''; }
  set kRating(v: string) { this.filterState.selectedAmBest = v ? this.amBestAtOrAbove(v) : []; this.applyNow(); }

  // Context-only knobs (no product filter) — local working values seeded from
  // the case; editable + saved. The (b) "doesn't filter products" cue is in the template.
  private readonly _caseAge = signal<number | null>(null);
  private readonly _casePremium = signal<number | null>(null);
  get kAge(): number | null { return this._caseAge(); }
  set kAge(v: number | null) { this._caseAge.set(this.numOrNull(v)); }
  get kPremium(): number | null { return this._casePremium(); }
  set kPremium(v: number | null) { this._casePremium.set(this.numOrNull(v)); }

  private surrRange(): { min: number | null; max: number | null } {
    // The Unlimited sentinel (-3) is NOT a year, so it must stay out of the
    // Criteria Min/Max arithmetic. Left in, it made the Min box read "-3", made
    // setSurrRange() expand a bogus -3..N range, and made criteriaToInput()
    // persist -3 as the case's termMin.
    const nums = this.filterState.selectedSurrYears
      .filter((s) => s !== SURR_YEARS_UNLIMITED)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
    if (!nums.length) return { min: null, max: null };
    return { min: Math.min(...nums), max: Math.max(...nums) };
  }
  private setSurrRange(min: number | null, max: number | null): void {
    // Editing the numeric range must not silently drop an Unlimited selection —
    // it isn't part of the range, so it survives independently.
    const keepUnlimited = this.filterState.selectedSurrYears.includes(SURR_YEARS_UNLIMITED);
    if (min == null && max == null) {
      this.filterState.selectedSurrYears = keepUnlimited ? [SURR_YEARS_UNLIMITED] : [];
      this.applyNow();
      return;
    }
    const lo = min ?? max as number, hi = max ?? min as number;
    const years: string[] = [];
    for (let y = Math.min(lo, hi); y <= Math.max(lo, hi); y++) years.push(String(y));
    if (keepUnlimited) years.push(SURR_YEARS_UNLIMITED);
    this.filterState.selectedSurrYears = years;
    this.applyNow();
  }
  private amBestAtOrAbove(floor: string): string[] {
    const i = this.AM_BEST_ORDER.indexOf(floor);
    return i < 0 ? [floor] : this.AM_BEST_ORDER.slice(0, i + 1);
  }
  private ratingFloorFromSet(set: ReadonlyArray<string>): string | null {
    let best = -1;
    for (const r of set) { const i = this.AM_BEST_ORDER.indexOf(r); if (i > best) best = i; }
    return best >= 0 ? this.AM_BEST_ORDER[best] : null;
  }

  protected readonly hasAnyCriteria = computed<boolean>(() => {
    this.filterState.version();
    return !!this.kState || this.kAge != null || this.kPremium != null
      || this.kTermMin != null || this.kTermMax != null || !!this.kRating;
  });
  protected readonly caseModified = computed<boolean>(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return false;
    this.filterState.version();
    return (c.state ?? '') !== this.kState
      || (c.age ?? null) !== this._caseAge()
      || (c.premium ?? null) !== this._casePremium()
      || (c.termMin ?? null) !== this.kTermMin
      || (c.termMax ?? null) !== this.kTermMax
      || (((c.ratingFloor as string | null) ?? '')) !== this.kRating;
  });
  protected readonly fitCount = computed<number>(() => this.filterState.caseFitCount());
  protected readonly totalCount = computed<number>(() => this.filterState.totalCount());

  // Case lens mode (Fit only / Show all) — shared via CaseContextService so the
  // marker, knob disable-state, and count all agree. Mirrors rates-rail.
  protected readonly caseAllMode = computed<boolean>(() => this.caseCtx.caseViewMode() === 'all');
  setCaseFit(): void { this.caseCtx.setCaseViewMode('fit'); }
  setCaseAll(): void { this.caseCtx.setCaseViewMode('all'); }

  protected readonly isPersona = computed<boolean>(() => {
    const c = this.caseCtx.activeCase();
    return !!c && c.clientId == null;
  });

  // Case picker (load / switch / clear).
  protected readonly caseList = signal<Case[]>([]);
  protected readonly boundCases = computed(() => this.caseList().filter((c) => c.clientId != null));
  protected readonly personaCases = computed(() => this.caseList().filter((c) => c.clientId == null));
  protected readonly activeCaseId = computed<number | null>(() => this.caseCtx.activeCase()?.id ?? null);
  private loadCaseList(): void { this.caseApi.list().subscribe({ next: (rows) => this.caseList.set(rows) }); }
  /** Open the full case editor, returning here when done (returnTo). */
  editCase(): void {
    const id = this.caseCtx.activeCase()?.id;
    if (id == null) return;
    this.router.navigate(['/broker/cases', id], { queryParams: { returnTo: this.router.url } });
  }

  onCasePick(id: number | null): void {
    if (id == null) { this.caseCtx.clear(); return; }
    if (id === this.caseCtx.activeCase()?.id) return;
    this.caseApi.get(id).subscribe({ next: (c) => this.caseCtx.setActive(c) });
  }

  resetToCase(): void {
    this.filterState.requestSeedFromCase();
    const c = this.caseCtx.activeCase();
    this._caseAge.set(c?.age ?? null);
    this._casePremium.set(c?.premium ?? null);
  }
  clearCriteria(): void {
    this.filterState.companyFilter = null;
    this.filterState.selectedStates = [];
    this.filterState.selectedSurrYears = [];
    this.filterState.selectedAmBest = [];
    this._caseAge.set(null);
    this._casePremium.set(null);
    this.openGroupKey.set(null);
    this.closeCarrierSub();
    this.applyNow();
  }
  saveToCase(): void {
    const c = this.caseCtx.activeCase();
    if (!c) return;
    this.caseApi.update(c.id, this.criteriaToInput(c.name, c.label)).subscribe({
      next: () => this.caseApi.get(c.id).subscribe((fresh) => this.caseCtx.setActive(fresh)),
    });
  }
  saveAsPersona(): void {
    const name = (window.prompt('Name this persona', 'New persona') ?? '').trim();
    if (!name) return;
    this.caseApi.createPersona(name, this.criteriaToInput(name)).subscribe({
      next: ({ id }) => this.caseApi.get(id).subscribe((fresh) => this.caseCtx.setActive(fresh)),
    });
  }
  promoteToCase(): void {
    const c = this.caseCtx.activeCase();
    if (!c) return;
    const clientName = (window.prompt('Client name for this case', '') ?? '').trim();
    if (!clientName) return;
    this.caseApi.promote(c.id, clientName, this.criteriaToInput(clientName)).subscribe({
      next: () => this.caseApi.get(c.id).subscribe((fresh) => this.caseCtx.setActive(fresh)),
    });
  }
  private criteriaToInput(name: string, label?: string | null): CaseInput {
    const r = this.surrRange();
    return {
      name,
      state: this.kState || null,
      age: this.kAge,
      premium: this.kPremium,
      termMin: r.min,
      termMax: r.max,
      ratingFloor: (this.kRating || null) as RatingFloor,
      label: label ?? undefined,
    };
  }

  /** Currently-focused carrier id when the fly-out is open. */
  protected readonly flyoutCarrierId = signal<string | null>(null);

  /** 2026-06-04 search-consolidation: the rail-top "Search products or
   *  carriers…" input was removed in favour of a single page-header search
   *  (products.component.html). This computed re-reads filterState.searchText
   *  on every version bump so the companies-only picker's
   *  [externalSearchQuery] stays in sync as the user types. */
  protected readonly pageSearchText = computed<string>(() => {
    this.filterState.version();
    return this.filterState.searchText ?? '';
  });

  constructor() {
    // P3: load case list, and on active-case change reload it + reseed the
    // context-only Age/Premium knobs (the filtering dims are reseeded by the
    // products component's auto-seed effect).
    this.loadCaseList();
    effect(() => {
      const c = this.caseCtx.activeCase();
      untracked(() => {
        this.loadCaseList();
        this._caseAge.set(c?.age ?? null);
        this._casePremium.set(c?.premium ?? null);
      });
    });

    // Active types change → close the fly-out. The picker data tree
    // depends on activeTypes (different carriers visible), so an open
    // fly-out anchored against the old tree would be stale.
    effect(() => {
      this.activeTypes(); // touch
      if (this.flyoutSvc.isOpen()) this.flyoutSvc.close();
    });

    // Single-expand accordion: opening a different group destroys the
    // Carrier group body (it's @if-rendered). Close the fly-out when the
    // Carrier group is no longer the open one.
    effect(() => {
      const open = this.openGroupKey();
      if (open && open !== 'carrier' && this.flyoutSvc.isOpen()) {
        this.flyoutSvc.close();
      }
    });

    // Carrier sub-item close → close the fly-out (fly-out is anchored to
    // a row that no longer exists in the DOM).
    effect(() => {
      if (!this.carrierSubOpen() && this.flyoutSvc.isOpen()) {
        this.flyoutSvc.close();
      }
    });

    // Hiding the rail closes it too. The fly-out is a CDK overlay attached
    // to document.body, NOT a child of the rail, so the rail's own
    // `visibility: hidden` does not reach it -- it would be left floating
    // beside a 44px strip with nothing to explain it.
    effect(() => {
      if (this.railLayout.collapsed() && this.flyoutSvc.isOpen()) {
        this.flyoutSvc.close();
      }
    });

    // Collapse a section when its Active-Filters chip is cleared
    // (clearAppliedDimension → dimClearedRequest). Company → the Carriers
    // sub-accordion; the individual pickers self-collapse (see
    // ProductChecklistPickerComponent). Distinct from unchecking within a
    // picker, which leaves the section open.
    effect(() => {
      const req = this.filterState.dimClearedRequest();
      if (req.tick === 0) return;
      untracked(() => {
        if (req.key === 'company') this.closeCarrierSub();
      });
    });
  }

  /**
   * Row click from the in-rail companies-only picker.
   *
   * The picker now expands the carrier's products inline and owns that state
   * itself, so there is nothing to open here. Kept as the (rowClick) sink
   * because the rail still tracks which carrier is open for its own summary.
   */
  onCarrierRowClick(ev: { carrierId: string; el: HTMLElement }): void {
    this.flyoutCarrierId.set(
      this.flyoutCarrierId() === ev.carrierId ? null : ev.carrierId,
    );
  }

  protected readonly carrierSubOpen = signal(false);
  toggleCarrierSub(): void {
    this.carrierSubOpen.update(v => !v);
  }

  // 4-up multi-select pills. Click toggles inclusion in activeTypes.
  // Same visual shape as /rates pill tabs but multi-select semantics.
  protected readonly types: ReadonlyArray<ProductType> = [
    { id: 'IA',   label: 'IA' },
    { id: 'FA',   label: 'FA' },
    { id: 'VA',   label: 'VA' },
    { id: 'RILA', label: 'RILA' },
  ];

  /** activeTypes signal — touches version() for change-detection. */
  protected readonly activeTypes = computed<Set<string>>(() => {
    this.filterState.version();
    return this.filterState.activeTypes;
  });

  /** Per-type product count badges — re-evaluates on every state
   *  mutation. The component owns typeCounts (computed from filtered
   *  rows); rail reads via computed. */
  protected readonly typeCount = (id: string): number => {
    // Phase 4.2 ships without count integration; 4.3+ wire to the
    // component's typeCounts Record. Return 0 for now.
    return 0;
  };

  /** Click handler for a type pill — toggles inclusion. The default
   *  state has all 4 types selected; clicking when all 4 are active
   *  isolates to just that one (single-pill-becomes-active gesture).
   *  Otherwise toggle as expected. */
  onTypeClick(id: string): void {
    const set = new Set(this.filterState.activeTypes);
    const ALL_TYPES = ['FA', 'IA', 'VA', 'RILA'];
    if (set.size === ALL_TYPES.length) {
      // All-active → isolate to this one
      set.clear();
      set.add(id);
    } else if (set.has(id)) {
      if (set.size > 1) {
        set.delete(id);
      } else {
        // Isolated to this one already → clicking it again restores all four.
        // Previously this branch did nothing "to keep at least one selected",
        // which left the user stranded: having isolated a type there was no way
        // back to the full catalogue short of reloading the page.
        ALL_TYPES.forEach(t => set.add(t));
      }
    } else {
      set.add(id);
    }
    this.filterState.activeTypes = set;
    this.filterState.requestApplyFilters();
  }

  // 2026-06-04 search-consolidation: onRailSearchInput / onRailSearchClear
  // removed alongside the rail-top input. The page-header input on
  // products.component.html owns the keystrokes now; it routes through
  // ProductsComponent.onSearchInput → filterState.searchText and the
  // existing 150ms grid quickFilter debounce.

  // ── Applied-state surfacing ──────────────────────────────────────
  // 2026-06-12 parity: the in-rail applied-chip stack was removed to match
  // the rates rail (which moved applied-state to the page-header footprints
  // on 2026-06-01). Products' page-header already renders .ph-footprints, so
  // the in-rail stack was double-surfacing. buildAppliedProductChips /
  // AppliedChip / onChipClear dropped alongside it.

  // ── Toggle pills (Phase 4.4 — My Shelf only) ─────────────────────
  // /rates' rail had 4 booleans (ROP/Bail/Bonus/Inverse) as discrete
  // boolean fields. /products doesn't have those as boolean fields —
  // ROP/MVA/Bonus live in `selectedRiders: string[]`, so they're
  // managed via the picker (deferred from 4.3). My Shelf is the only
  // toggle that ships in 4.4.
  //
  // Shelf state mirrors /rates 3f pattern. /products is cross-product
  // so we use the FIRST active type for the shouldForceShelf check
  // (shelf access tends to be set globally for a user, not per-type).
  protected readonly shelfState = computed<'off' | 'on' | 'locked' | 'disabled'>(() => {
    if (!this.sessionCtx.hasSession()) return 'disabled';
    const firstType = Array.from(this.filterState.activeTypes)[0] ?? 'IA';
    if (this.sessionCtx.shouldForceShelf(firstType)) return 'locked';
    return this.sessionCtx.shelfMode() ? 'on' : 'off';
  });

  /** Should the Scope block render at all?
   *
   *  Shown when at least one product type offers a choice — 'all' or 'd_shelf'.
   *  Hidden only when every category is 'shelf' (locked, no choice). The whole
   *  rail-scope group goes, not just the switch: unlike the rates rail this
   *  group holds nothing else, so keeping it would leave an orphan "Scope"
   *  heading.
   *
   *  /products is cross-product with no per-type tab, so this checks all four
   *  types rather than the active ones — matching ProductsComponent's
   *  `shelfToggleMode`, so the rail and the page chrome can't disagree.
   *
   *  Enforcement is per-row regardless: passesShelfScope() keys off each row's
   *  own productType, so a 'shelf' type stays scoped even while the toggle is
   *  shown for a different 'd_shelf' type. */
  protected readonly showShelfScope = computed(() =>
    !this.sessionCtx.hasSession()
    || ['VA', 'FA', 'IA', 'RILA'].some(t => this.sessionCtx.shouldShowToggle(t)));

  /** My Shelf click — early-return if locked/disabled. Otherwise
   *  bump shelfToggleRequest; component effect runs the existing
   *  onShelfToggle chain (toggleShelfMode + applyFilters + refresh). */
  onShelfClick(): void {
    const s = this.shelfState();
    if (s === 'locked' || s === 'disabled') return;
    this.filterState.requestShelfToggle();
  }

  // ── Phase 12 — inline filter groups ──────────────────────────────

  /** Single-expand accordion: only one category group open at a time. */
  protected readonly openGroupKey = signal<string | null>(null);
  onGroupExpandRequest(key: string): void {
    if (key !== 'carrier') this.closeCarrierSub();
    this.openGroupKey.set(key);
  }

  /** Called when the Company & Product group is user-collapsed or another
   *  group opens, so the Carriers submenu resets to closed. */
  onCarrierGroupCollapse(): void {
    this.closeCarrierSub();
  }

  private closeCarrierSub(): void {
    this.carrierSubOpen.set(false);
    if (this.flyoutSvc.isOpen()) this.flyoutSvc.close();
    this.flyoutCarrierId.set(null);
  }

  /** Option list for a picker dimension (bridged from the page via
   *  ProductsFilterStateService.setFilterOptions). */
  protected options(dim: string): string[] {
    return this.filterState.filterOptions()[dim] ?? [];
  }

  onPickerCloseRequest(_dim: string): void {
    // No-op: the group disclosure owns open/closed. Pickers collapse
    // with the group (Phase 12 has no pin model, so no per-picker
    // collapse-without-unpin to wire).
  }

  // Per-group value summaries (badge) + active counts (auto-expand).

  // 2026-06-12 parity (#5): the Company & Product group badge now shows the
  // carrier-only brand-coalesced summary (carrierLauncherSummary), matching the
  // rates rail's Company & Product header (companyFilterSummary). The former
  // carrierBadge() crammed A.M. Best / Premium Type / Share Class / Status into
  // the same slot — rates shows none of those in the group header — so it was
  // dropped along with the dead productBadge()/productActive() computeds (the
  // Product group was merged into Company & Product earlier, leaving no binding).

  /** Compact summary shown next to the "Carrier" group header — same
   *  pattern as the rates rail. Counts distinct actual company names. */
  protected readonly carrierLauncherSummary = computed<{ label: string; count: number | null }>(() => {
    this.filterState.version();
    const cf = this.filterState.slot.companyFilter;
    if (!cf || (cf.companies.length === 0 && cf.products.length === 0)) {
      return { label: 'All', count: null };
    }
    const c = new Set(cf.companies).size;
    const p = cf.products.length;
    if (c > 0 && p === 0) return { label: `${c} ${c === 1 ? 'company' : 'companies'}`, count: c };
    if (c === 0 && p > 0) return { label: `${p} ${p === 1 ? 'product' : 'products'}`, count: p };
    return {
      label: `${c} ${c === 1 ? 'company' : 'companies'} · ${p} ${p === 1 ? 'product' : 'products'}`,
      count: c,
    };
  });

  /** PickerData grouped by ACTUAL company name (the carrierProductTree key,
   *  i.e. companyNa), NOT a brand alias — each distinct legal company is its
   *  own picker row.
   *
   *  Product ids are namespaced per-company (`${company}::${productName}`) —
   *  NOT the bare product name. Several carriers market identically-named
   *  products; with a bare-name id, the picker's single flat selected-
   *  products Set would treat that shared name as selected under BOTH
   *  carriers, so checking one carrier's box made others show as selected
   *  too, and their names leaked into the company filter. */
  protected readonly pickerData = computed<PickerData>(() => {
    this.filterState.version();
    const tree = this.filterState.carrierProductTree();
    const companyNames = Object.keys(tree).sort();
    const carriers: PickerCarrier[] = companyNames.map((company) => {
      const products: PickerProduct[] = [...new Set(tree[company] ?? [])]
        .sort()
        .map<PickerProduct>((p) => ({ id: this.toPickerProductId(company, p), name: p }));
      return {
        id: company,
        name: company,
        logoText: this.deriveLogoText(company),
        products,
      };
    });
    return { carriers };
  });

  private static readonly PICKER_ID_SEP = '::';

  private toPickerProductId(company: string, product: string): string {
    return `${company}${ProductSearchRailComponent.PICKER_ID_SEP}${product}`;
  }

  private productNameFromPickerId(id: string): string {
    const idx = id.indexOf(ProductSearchRailComponent.PICKER_ID_SEP);
    return idx === -1 ? id : id.slice(idx + ProductSearchRailComponent.PICKER_ID_SEP.length);
  }

  /** Pre-checked product ids for the picker's initialSelection.
   *  Must read version() explicitly — filterState.slot is a plain mutable
   *  object (not a signal), so without this read the computed never recomputes
   *  when the company filter is cleared externally (e.g. via chip ×).
   *
   *  companyFilter.products holds bare product names (no carrier scoping),
   *  so reconstruct picker-internal composite ids by cross-referencing which
   *  of the SELECTED companies actually carry that product name in
   *  pickerData(). This keeps re-seeding the picker from marking a
   *  same-named product selected under a carrier the user never checked. */
  protected readonly pickerInitialSelection = computed(() => {
    this.filterState.version();
    const cf = this.filterState.slot.companyFilter;
    if (!cf) return { selectedProductIds: [] };
    const productNames = new Set(cf.products);
    const selectedCompanies = new Set(cf.companies);
    const out: string[] = [];
    for (const carrier of this.pickerData().carriers) {
      if (!selectedCompanies.has(carrier.id)) continue;
      for (const p of carrier.products) {
        if (productNames.has(p.name)) out.push(p.id);
      }
    }
    return { selectedProductIds: out };
  });

  private deriveLogoText(name: string): string {
    const words = name.replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean);
    if (words.length === 0) return '??';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  /** Live-apply on every selection change. Carrier ids are the actual company
   *  names (grouping is no longer brand-aliased), so they go straight into
   *  companyFilter.companies — applyFilters compares those against the row's
   *  own company name. */
  onPickerSelectionChange(r: PickerApplyResult): void {
    const companies = [...r.selectedCarrierIds];
    // r.selectedProductIds are picker-internal composite ids
    // (`${company}::${productName}`) — strip back to bare names for
    // companyFilter.products, which applyFilters compares against the
    // row's own product name.
    const bareProductNames = [...new Set(r.selectedProductIds.map((id) => this.productNameFromPickerId(id)))];
    if (companies.length === 0 && bareProductNames.length === 0) {
      this.filterState.companyFilter = null;
    } else {
      this.filterState.companyFilter = { companies, products: bareProductNames };
    }
    this.filterState.requestApplyFilters();
    // Keep an already-open fly-out in sync — this handler can fire from the
    // left list's own carrier checkbox, which doesn't go through
    // rowClick/reanchor, so the fly-out would otherwise keep showing its
    // stale pre-toggle checkboxes. Pass the composite ids straight through —
    // the fly-out's own picker instance expects the same id format.
    if (this.flyoutSvc.isOpen()) {
      this.flyoutSvc.refreshSelection(r.selectedProductIds);
    }
  }
  protected readonly carrierActive = computed<number>(() => {
    this.filterState.version();
    const s = this.filterState.slot;
    // Merged "Company & Product" group counts: company/product picker + amBest +
    // former Product dims (premiumType, shareClass, status).
    return (s.companyFilter?.companies.length || s.companyFilter?.products.length ? 1 : 0)
      + (s.selectedAmBest.length ? 1 : 0)
      + (s.selectedPremiumTypes.length ? 1 : 0)
      + (s.selectedShareClasses.length ? 1 : 0)
      + (s.filterStatus !== 'all' ? 1 : 0);
  });

  /** Slider track ceilings, published by ProductsComponent after data load. */
  /** Surr Years display overrides (-3 → "Unlimited") for the picker below. */
  protected readonly surrYearsLabels = SURR_YEARS_LABELS;

  protected readonly termBadge = computed<string>(() => {
    this.filterState.version();
    const s = this.filterState.slot;
    const parts: string[] = [];
    if (s.selectedSurrYears.length) {
      // The Unlimited sentinel takes no "y" suffix — "-3y" / "Unlimitedy".
      parts.push(
        s.selectedSurrYears.length === 1
          ? (s.selectedSurrYears[0] === SURR_YEARS_UNLIMITED
              ? surrYearsLabel(s.selectedSurrYears[0])
              : `${s.selectedSurrYears[0]}y`)
          : `${s.selectedSurrYears.length} yrs`
      );
    }
    if (s.selectedPremium != null) parts.push(formatPremium(s.selectedPremium));
    return parts.length ? parts.join(' · ') : 'All';
  });
  protected readonly termActive = computed<number>(() => {
    this.filterState.version();
    const s = this.filterState.slot;
    let n = 0;
    if (s.selectedSurrYears.length) n++;
    if (s.selectedPremium != null) n++;
    return n;
  });

  protected readonly featuresBadge = computed<string>(() => {
    this.filterState.version();
    const r = this.filterState.slot.selectedRiders;
    // "All" not "None" when nothing is selected — the other three filter groups
    // all summarise an empty selection as "All", and "None" read as "no riders
    // are available" rather than "no riders chosen".
    return r.length ? (r.length === 1 ? r[0] : `${r.length} riders`) : 'All';
  });
  protected readonly featuresActive = computed<number>(() => {
    this.filterState.version();
    return this.filterState.slot.selectedRiders.length ? 1 : 0;
  });

  protected readonly geographyBadge = computed<string>(() => {
    this.filterState.version();
    const st = this.filterState.slot.selectedStates;
    return st.length ? (st.length === 1 ? st[0] : `${st.length} states`) : 'All';
  });
  protected readonly geographyActive = computed<number>(() => {
    this.filterState.version();
    return this.filterState.slot.selectedStates.length ? 1 : 0;
  });

  /** Selected state codes for the State Availability grid, which the group
   *  renders directly rather than through a picker row. */
  protected readonly selectedStates = computed<string[]>(() => {
    this.filterState.version();
    const v = this.filterState.slot.selectedStates;
    return Array.isArray(v) ? v : [];
  });

  /** Same multi-select write ProductChecklistPickerComponent made for this
   *  field — setSlotField, then ask the page to re-run its grid filter. */
  toggleState(code: string): void {
    const cur = this.selectedStates();
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    this.filterState.setSlotField('selectedStates', next);
    this.filterState.requestApplyFilters();
  }

  // Status 3-way toggle (Product group).
  protected readonly statusValue = computed<'all' | 'open' | 'closed'>(() => {
    this.filterState.version();
    return this.filterState.slot.filterStatus;
  });
  onStatusClick(s: 'all' | 'open' | 'closed'): void {
    this.filterState.filterStatus = s;
    this.filterState.requestApplyFilters();
  }

  // 2026-06-04 refinement #1: find-a-filter (Phase 12.2) removed —
  // FIELD_SEARCH_INDEX, RAIL_GROUPS, findFilterQuery / findFilterMatches /
  // findFilterActive / findFilterRaw / fieldVisible / groupVisible /
  // fieldMatchesQuery were all dropped alongside the rates rail's
  // "Filter the filters" cleanup. The pickers' own scoped search inputs
  // (commit bc411d40, plus each ProductChecklistPicker's built-in search
  // for >8 options) remain functional.
}
